/**
 * Controls the header Upgrade button from the shared subscription snapshot and
 * the already-resolved upgrade-prompts feature toggle.
 */

import {
  fetchSubscriptionStatus,
  getSubscriptionSnapshot,
  isPaidPlan,
  planFromStatus
} from '../core/subscriptionStatus.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';

export class HeaderUpgradeController {
  /**
   * @param {Object} options
   * @param {HTMLElement|null} options.button - Header Upgrade button
   * @param {Object} options.eventBus - App event bus
   * @param {Object} options.featureToggleService - Resolved feature-toggle source
   */
  constructor({ button, eventBus, featureToggleService }) {
    this.button = button;
    this.eventBus = eventBus;
    this.featureToggleService = featureToggleService;
    this.subscriptionKnown = false;
    this.basicPlan = false;
    this.upgradePromptsEnabled = false;
    this.initialized = false;

    this.handleSubscriptionStatus = this.handleSubscriptionStatus.bind(this);
    this.handleToggleChange = this.handleToggleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleUserUpdated = this.handleUserUpdated.bind(this);
  }

  /** Initialize signal subscriptions, button behavior, and the shared status fetch. */
  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    this.upgradePromptsEnabled = this.featureToggleService?.get('upgradePrompts') === true;
    this.eventBus.on('subscription:status', this.handleSubscriptionStatus);
    this.eventBus.on('toggle:upgradePrompts', this.handleToggleChange);
    this.eventBus.on('user:updated', this.handleUserUpdated);
    this.button?.addEventListener('click', this.handleClick);

    const existingStatus = getSubscriptionSnapshot();
    if (existingStatus) this.handleSubscriptionStatus(existingStatus);
    this.applyVisibility();
    // GET /subscription/status is requireAuth. A guest skips it and stays
    // hidden (`subscriptionKnown` false); `user:updated` re-arms the fetch.
    if (window.app?.currentUser?.isGuest) return;
    fetchSubscriptionStatus();
  }

  /**
   * Fetch the subscription status once a guest has converted. Emitted by
   * `handleGuestConversion`; `app.currentUser` is already the account by then.
   * No `force`: the TTL short-circuit only fires when a snapshot exists, and a
   * guest never fetched one. A null result leaves the button hidden.
   */
  handleUserUpdated() {
    if (window.app?.currentUser?.isGuest) return;
    fetchSubscriptionStatus();
  }

  /**
   * Apply a confirmed subscription status.
   * @param {Object|null} status - Shared subscription status payload
   */
  handleSubscriptionStatus(status) {
    if (!status) return;
    this.subscriptionKnown = true;
    this.basicPlan = !isPaidPlan(planFromStatus(status));
    this.applyVisibility();
  }

  /**
   * Apply the resolved upgrade-prompts feature toggle.
   * @param {Object} data
   * @param {boolean} data.enabled
   */
  handleToggleChange({ enabled } = {}) {
    this.upgradePromptsEnabled = enabled === true;
    this.applyVisibility();
  }

  /** Navigate through the shared upgrade analytics/navigation contract. */
  handleClick() {
    goToUpgrade(this.eventBus, 'headerButton');
  }

  /** @private */
  applyVisibility() {
    if (!this.button) return;
    const shouldShow = this.subscriptionKnown && this.basicPlan && this.upgradePromptsEnabled;
    this.button.classList.toggle('feature-toggle--hidden', !shouldShow);
    if (shouldShow) this.button.style.display = '';
  }

  /** Remove all event-bus and DOM listeners. */
  destroy() {
    if (!this.initialized) return;
    this.eventBus.off('subscription:status', this.handleSubscriptionStatus);
    this.eventBus.off('toggle:upgradePrompts', this.handleToggleChange);
    this.eventBus.off('user:updated', this.handleUserUpdated);
    this.button?.removeEventListener('click', this.handleClick);
    this.initialized = false;
  }
}

export default HeaderUpgradeController;
